import { cn } from '@planuze/ui';
import { useSupportContent } from '../../content/sections/support.js';
import { fmtDateTime } from '../../lib/format.js';
import type { ChatMessage as ChatMessageType } from '../../stores/chat-store.js';

/** Schemes seguros para anexo clicável (mesma allowlist de `message-renderer.tsx` do
 *  agent-chat). O write-side já restringe attachments ao host do R2 (`r2AttachmentUrl`
 *  em `packages/admin-contracts/src/chat.ts`), mas o `messageDto` LIDO aqui não valida
 *  scheme/host (`z.array(z.string())`) — o guard de render é a última linha de defesa
 *  contra um write-path futuro que pule aquela validação (`javascript:` disfarçado de
 *  nome de arquivo via `.split('/').pop()` seria XSS armazenado no operador de suporte). */
const SAFE_ATTACHMENT_SCHEMES = new Set(['http:', 'https:', 'mailto:']);

function isSafeAttachmentHref(href: string): boolean {
  try {
    return SAFE_ATTACHMENT_SCHEMES.has(new URL(href, 'https://example.invalid').protocol);
  } catch {
    return false;
  }
}

/* Bolha de mensagem. Conteúdo em `whitespace-pre-wrap` (texto puro — sem
   dangerouslySetInnerHTML, anti-XSS). Staff à direita, usuário à esquerda. */
export function ChatMessage({ message }: { readonly message: ChatMessageType }) {
  const copy = useSupportContent();
  const isStaff = message.senderType === 'staff';
  return (
    <div className={cn('flex min-w-0', isStaff ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'min-w-0 max-w-[70%] rounded-2xl px-4 py-2',
          isStaff ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
        )}
      >
        <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-sm">
          {message.content}
        </p>
        {message.attachments.length > 0 && (
          <ul className="mt-2 min-w-0 space-y-1">
            {message.attachments.map((url) =>
              isSafeAttachmentHref(url) ? (
                <li key={url} className="min-w-0">
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="block max-w-full break-words text-xs underline [overflow-wrap:anywhere]"
                  >
                    {url.split('/').pop()}
                  </a>
                </li>
              ) : (
                <li key={url} className="min-w-0">
                  <span className="block max-w-full break-words text-xs [overflow-wrap:anywhere]">
                    {url.split('/').pop()}
                  </span>
                </li>
              ),
            )}
          </ul>
        )}
        <span className="mt-1 block text-xs opacity-70">
          {fmtDateTime(message.createdAt)}
          {message.pending === true ? copy.chatMessage.sendingSuffix : ''}
        </span>
      </div>
    </div>
  );
}
